'use client';

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@coas/ui/dialog';
import { cn } from '@coas/ui/lib';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@coas/ui/select';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { api } from 'trpc/react';
import { useInvalidateAll } from '~/features/Cache';
import { Spinner } from '~/shared/ui/spinner';

type DiscountType = 'None' | 'Fake' | 'Real';

type Props = {
    products: ReadonlyArray<{ id: string }>;
    open: boolean;
    onOpenChange: (open: boolean, clear?: boolean) => void;
};

type FormValues = {
    discountType: DiscountType;
    discountPercent?: number;
};

export const ProductSetDiscountDialog: React.FC<Props> = ({ products, open, onOpenChange }) => {
    const productIds = products.map(p => p.id);

    const {
        register,
        handleSubmit,
        reset,
        clearErrors,
        watch,
        formState: { errors },
        control,
    } = useForm<FormValues>({
        defaultValues: { discountType: 'None', discountPercent: 0 },
        mode: 'onTouched',
    });

    const discountType = watch('discountType');
    const discountPercent = watch('discountPercent');

    const { mutate: invalidateAll } = useInvalidateAll();

    const { mutate, isPending } = api.product.setDiscountMany.useMutation({
        onMutate: () => {
            toast.loading('Применяем скидки...', { id: 'setDiscountMany' });
        },
        onSuccess: () => {
            invalidateAll();
            onOpenChange(false, true);
            toast.success('Скидки применены', { id: 'setDiscountMany' });
        },
        onError: () => {
            toast.error('Не удалось применить скидки', { id: 'setDiscountMany' });
        },
    });

    useEffect(() => {
        if (!open) return;
        reset({ discountType: 'None', discountPercent: 0 });
        clearErrors();
    }, [open, reset, clearErrors]);

    const blockCloseIfLoading = (e: Event) => {
        if (isPending) e.preventDefault();
    };

    const percentRequired = discountType !== 'None';
    const percentIsInt = Number.isInteger(discountPercent as number);
    const percentInRange =
        typeof discountPercent === 'number' &&
        !Number.isNaN(discountPercent) &&
        discountPercent >= 0 &&
        discountPercent <= 100 &&
        percentIsInt;

    const canSubmit = productIds.length > 0 && !isPending && (!percentRequired || percentInRange);

    return (
        <Dialog
            open={open || isPending}
            onOpenChange={next => {
                if (isPending) return;
                onOpenChange(next);
            }}
        >
            <DialogContent
                className={cn(
                    'z-[100] w-[min(92vw,580px)] rounded-12 border border-neutral-200 bg-neutral-50 text-neutral-950 shadow-lg outline-none',
                    'max-h-[min(80vh,calc(var(--visual-viewport-height)-2rem))] overflow-hidden',
                    'p-0',
                    '[&>button]:right-4 [&>button]:top-4 md:[&>button]:right-6 md:[&>button]:top-6',
                    '[&>button]:z-10',
                )}
                onEscapeKeyDown={blockCloseIfLoading}
                onPointerDownOutside={blockCloseIfLoading}
                onInteractOutside={blockCloseIfLoading}
            >
                <div className='flex max-h-full flex-col gap-4 px-4 py-4 md:px-6 md:py-6'>
                    <DialogHeader className='text-left pr-10'>
                        <div className='flex flex-col gap-2'>
                            <DialogTitle className='text-18 font-600 text-neutral-950 md:text-22'>
                                Установить скидку для выбранных товаров
                            </DialogTitle>

                            <DialogDescription className='text-left text-18 leading-snug text-neutral-500'>
                                Будут обновлены все размеры у {productIds.length} товар(ов)
                            </DialogDescription>
                        </div>
                    </DialogHeader>

                    <form
                        onSubmit={handleSubmit(data => {
                            mutate({
                                ids: productIds,
                                discountType: data.discountType,
                                discountPercent: data.discountType === 'None' ? undefined : (data.discountPercent ?? 0),
                            });
                        })}
                        className='flex flex-col gap-4'
                    >
                        <div className='flex flex-col gap-1 text-16'>
                            <label htmlFor='discountType' className='text-16'>
                                Тип скидки:
                            </label>

                            <Controller
                                name='discountType'
                                control={control}
                                render={({ field }) => (
                                    <Select value={field.value} onValueChange={field.onChange}>
                                        <SelectTrigger
                                            id='discountType'
                                            className={cn(
                                                'w-full rounded-8 border border-neutral-300 bg-white px-2 py-1 text-16 text-neutral-900',
                                                'outline-none ring-0 focus:border-neutral-800',
                                            )}
                                        >
                                            <SelectValue placeholder='Выберите тип' />
                                        </SelectTrigger>

                                        <SelectContent
                                            className={cn(
                                                'bg-white text-neutral-900',
                                                'border border-neutral-200 rounded-8 shadow-lg',
                                                'z-[200]',
                                            )}
                                        >
                                            <SelectItem value='None'>Нет скидки</SelectItem>
                                            <SelectItem value='Fake'>Фиктивная</SelectItem>
                                            <SelectItem value='Real'>Реальная</SelectItem>
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </div>

                        <label className='flex flex-col gap-1 text-16'>
                            Процент скидки:
                            <input
                                type='number'
                                min={0}
                                max={100}
                                step={1}
                                {...register('discountPercent', {
                                    valueAsNumber: true,
                                    validate: v => {
                                        if (!percentRequired) return true;
                                        if (v === undefined || Number.isNaN(v)) return 'Укажите процент скидки';
                                        if (!Number.isInteger(v)) return 'Только целые числа';
                                        if (v < 0 || v > 100) return 'Допустимо от 0 до 100';
                                        return true;
                                    },
                                })}
                                disabled={!percentRequired}
                                className={cn(
                                    'w-full rounded-8 border border-neutral-300 bg-white px-2 py-1 text-16 text-neutral-900',
                                    'outline-none ring-0 focus:border-neutral-800',
                                    !percentRequired && 'opacity-60',
                                )}
                            />
                            <span className='text-12 text-neutral-500'>
                                Обязателен для типов «Фиктивная» и «Реальная»
                            </span>
                            {errors.discountPercent && (
                                <span className='text-12 text-red-600'>{errors.discountPercent.message}</span>
                            )}
                        </label>

                        <DialogFooter className='p-0'>
                            <div className='flex flex-row items-center justify-end gap-2 sm:gap-3'>
                                <DialogClose asChild>
                                    <button
                                        type='button'
                                        onClick={() => onOpenChange(false)}
                                        disabled={isPending}
                                        className={cn(
                                            'h-9 px-3 text-13 md:px-4 md:text-14',
                                            'rounded-8 font-500 shrink-0',
                                            'bg-neutral-200 text-neutral-900',
                                            'transition-colors duration-150 hover:bg-neutral-300',
                                            'disabled:pointer-events-none disabled:opacity-60',
                                        )}
                                    >
                                        Отмена
                                    </button>
                                </DialogClose>

                                <button
                                    type='submit'
                                    disabled={!canSubmit}
                                    className={cn(
                                        'h-9 px-3 text-13 md:px-4 md:text-14',
                                        'rounded-8 font-700 shrink-0',
                                        'bg-neutral-950 text-white',
                                        'transition-opacity duration-150',
                                        'disabled:pointer-events-none disabled:opacity-60',
                                        isPending && 'animate-pulse',
                                    )}
                                >
                                    Применить скидку
                                </button>
                            </div>
                        </DialogFooter>
                    </form>
                </div>

                {isPending && (
                    <div className='absolute inset-0 z-50 flex items-center justify-center rounded-12 bg-neutral-50/70'>
                        <Spinner className='size-6' />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
